import { useCallback, useEffect, useMemo, useState, VFC } from 'react';

import { getVtmplvOptionLabel } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import ParamsCreateVerificationCustom from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsCreateVerificationCustom';
import ParamsLegacyDynamicFlowUpdateFields from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsLegacyDynamicFlowUpdateFields';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { ObjectPropertiesSchema } from 'dashboard/models/external-integration';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import {
  isCreateVerificationActionWithFieldMapping,
  WorkflowRunVerificationAction,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';

const getVtmplvOptions = (
  actionKey: WorkflowStepActionKey,
  verificationTemplateVersions: WorkflowActionMeta['verification-template-versions'] | undefined,
): OptionType[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let templateVersionType: VerificationTemplateVersion['type'] | undefined = null;
  switch (actionKey) {
    case WorkflowStepActionKey.CreateVerificationAamvaFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionAamva;
      break;
    case WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionCertificateKorea;
      break;
    case WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseEcbsv;
      break;
    case WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabase;
      break;
    case WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseAadhaar;
      break;
    case WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseStandard;
      break;
    case WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDigitalIdFinnishTrustNetwork;
      break;
    case WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDigitalIdOneId;
      break;
    case WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDigitalIdSingpass;
      break;
    case WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDigitalIdUkSharecode;
      break;
    case WorkflowStepActionKey.CreateVerificationDocumentFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDocument;
      break;
    case WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionEmailAddress;
      break;
    case WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionGovernmentId;
      break;
    case WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionGovernmentIdNfc;
      break;
    case WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionPhoneNumber;
      break;
    case WorkflowStepActionKey.CreateVerificationSelfieFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionSelfie;
      break;
    case WorkflowStepActionKey.RunVerificationDatabaseBusiness:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseBusiness;
      break;
    case WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseBusinessFootprint;
      break;
    case WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionBusinessWebsite;
      break;
    case WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabasePhoneCarrier;
      break;
    case WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseSerpro;
      break;
    case WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseTin;
      break;
    case WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry:
      templateVersionType = ApiModel.VerificationTemplateVersionDatabaseVatNumber;
      break;
    default: {
      if (actionKey != null) {
        captureMessage(
          `No vtmplv type found for key: ${actionKey} in ITE workflow editor stepConfigAction`,
          'warning',
        );
      }
      return [];
    }
  }
  return (verificationTemplateVersions ?? [])
    .filter((vtmplv) => vtmplv.type === templateVersionType)
    .map(({ name, token: value, type: vtmplvType }) => ({
      label: getVtmplvOptionLabel({
        name,
        type: vtmplvType as VerificationTemplateVersion['type'],
        token: value,
      }),
      value,
    }));
};

// Verification specific fields can be found in FE: `front-end/dashboard/models/verification.ts,`
// and backend on the verification models, i.e. app/models/verification/database.rb
// To determine which fields are required, check for the validations on backend model files

const getVerificationInputOptions = (
  actionKey: WorkflowStepActionKey,
): ObjectPropertiesSchema[] => {
  switch (actionKey) {
    case WorkflowStepActionKey.CreateVerificationAamvaFromInquiry:
      return [
        { label: 'Identification Number', value: 'identification-number', required: true },
        { label: 'Issuing Authority', value: 'issuing-authority', required: true },
        { label: 'First Name', value: 'name-first' },
        { label: 'Last Name', value: 'name-last' },
        { label: 'Birthday', value: 'birthdate' },
        { label: 'Issue Date', value: 'issue-date' },
        { label: 'Expiration Date', value: 'expiration-date' },
        { label: 'Postal Code', value: 'address-postal-code' },
      ];
    case WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry:
      return [
        { label: 'First Name', value: 'name-first', required: true },
        { label: 'Last Name', value: 'name-last', required: true },
        { label: 'Birthday', value: 'birthdate', required: true },
        { label: 'Identification Number', value: 'identification-number', required: true },
        { label: 'Email Address', value: 'email-address', required: true },
        { label: 'Middle Name', value: 'name-middle' },
      ];
    case WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry:
      return [
        { label: 'Country Code', value: 'country-code', required: true },
        { label: 'First Name', value: 'name-first' },
        { label: 'Middle Name', value: 'name-middle' },
        { label: 'Last Name', value: 'name-last' },
        { label: 'Birthday', value: 'birthdate' },
        { label: 'Address Line 1', value: 'address-street-1' },
        { label: 'Address Line 2', value: 'address-street-2' },
        { label: 'Address City', value: 'address-city' },
        { label: 'Address Subdivision', value: 'address-subdivision' },
        { label: 'Postal Code', value: 'address-postal-code' },
        { label: 'Identification Number', value: 'identification-number' },
        { label: 'Identification Class', value: 'identification-class' },
        { label: 'Identification Number Type', value: 'identification-number-type' },
        { label: 'Phone Number', value: 'phone-number' },
        { label: 'Email Address', value: 'email-address' },
        { label: 'Document Number', value: 'document-number' },
        { label: 'Document Expiry Date', value: 'document-expiry-date' },
        { label: 'Document Issuing Date', value: 'document-issuing-date' },
        { label: 'Document Issuing Subdivision', value: 'document-issuing-subdivision' },
      ];
    case WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry:
      return [
        { label: 'First Name', value: 'name-first', required: true },
        { label: 'Middle Name', value: 'name-middle' },
        { label: 'Last Name', value: 'name-last', required: true },
        { label: 'Birthday', value: 'birthdate', required: true },
      ];
    case WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry:
      return [
        { label: 'First Name', value: 'name-first', required: true },
        { label: 'Last Name', value: 'name-last', required: true },
        { label: 'Identification Number', value: 'identification-number', required: true },
      ];
    case WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry:
      return [
        { label: 'Sharecode', value: 'sharecode', required: true },
        { label: 'Birthday', value: 'birthdate', required: true },
        { label: 'Full Name for comparison', value: 'name-full-input', required: false },
      ];
    case WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry:
      return [{ label: 'Email Address', value: 'email-address', required: true }];
    case WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry:
      return [{ label: 'Phone Number', value: 'phone-number', required: true }];
    case WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry:
      return [
        { label: 'Phone Number', value: 'phone-number', required: true },
        { label: 'First Name', value: 'name-first' },
        { label: 'Last Name', value: 'name-last' },
        { label: 'Birthday', value: 'birthdate' },
        { label: 'Address Line 1', value: 'address-street-1' },
        { label: 'Address Line 2', value: 'address-street-2' },
        { label: 'Address City', value: 'address-city' },
        { label: 'Address Subdivision', value: 'address-subdivision' },
        { label: 'Postal Code', value: 'address-postal-code' },
        { label: 'Country Code', value: 'country-code' },
      ];
    case WorkflowStepActionKey.RunVerificationDatabaseBusiness:
      return [
        { label: 'Address Line 1', value: 'address-street-1', required: true },
        { label: 'Address Line 2', value: 'address-street-2' },
        { label: 'Address City', value: 'address-city', required: true },
        { label: 'Address Subdivision', value: 'address-subdivision', required: true },
        { label: 'Business Name', value: 'business-name', required: true },
        { label: 'Postal Code', value: 'address-postal-code', required: true },
        { label: 'Country Code', value: 'address-country-code', required: true },
        { label: 'Registration Number', value: 'registration-number' },
      ];
    case WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry:
      return [
        { label: 'Address Line 1', value: 'address-street-1', required: true },
        { label: 'Address Line 2', value: 'address-street-2' },
        { label: 'Address City', value: 'address-city', required: true },
        { label: 'Address Subdivision', value: 'address-subdivision', required: true },
        { label: 'Business Name', value: 'business-name', required: true },
        { label: 'Postal Code', value: 'address-postal-code', required: true },
        { label: 'Country Code', value: 'address-country-code', required: true },
      ];
    case WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry:
      return [
        { label: 'Business Name', value: 'business-name', required: true },
        { label: 'Website Url', value: 'website-url', required: true },
      ];
    case WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry:
      return [
        { label: 'VAT Number', value: 'vat-number', required: true },
        { label: 'Country Code', value: 'country-code', required: true },
        { label: 'Business Name', value: 'business-name' },
        { label: 'Address Line 1', value: 'address-street-1' },
        { label: 'Address Line 2', value: 'address-street-2' },
        { label: 'Address City', value: 'address-city' },
        { label: 'Address Subdivision', value: 'address-subdivision' },
        { label: 'Postal Code', value: 'address-postal-code' },
      ];
    default:
      return [];
  }
};

export interface ParamsCreateVerificationV1Props extends ParamsBaseProps {
  usableObjects: UsableObjectConfig[];
  verificationTemplateVersions: WorkflowActionMeta['verification-template-versions'] | undefined;
  errors: WorkflowError[];
  editable: boolean;
  updateActionParams: (newParams: WorkflowRunVerificationAction['params']) => void;
}

const ParamsCreateVerificationV1: VFC<ParamsCreateVerificationV1Props> = ({
  step,
  usableObjects,
  verificationTemplateVersions,
  errors,
  editable,
  updateActionParams,
}) => {
  const action = step.action as WorkflowRunVerificationAction;
  const { key, params } = action;

  const { 'verification-template-version-id': verificationTemplateVersionId } = params;
  const [verificationTemplateVersion, setVerificationTemplateVersion] =
    useState<VerificationTemplateVersion>();
  useEffect(() => {
    if (verificationTemplateVersionId) {
      SessionClientDashboard.getResource<VerificationTemplateVersion>(
        ApiEndpoint.VerificationTemplateVersion,
        verificationTemplateVersionId,
      ).then(({ data }) => {
        setVerificationTemplateVersion(data);
      });
    }
  }, [verificationTemplateVersionId]);

  const verificationTemplateVersionIdTovTemplateId = useMemo<Record<string, string>>(() => {
    return (verificationTemplateVersions || []).reduce(
      (previousValue, currentValue) => {
        previousValue[currentValue.token] = currentValue.verificationTemplateId;
        return previousValue;
      },
      {} as Record<string, string>,
    );
  }, [verificationTemplateVersions]);
  const vtmplvOptions = useMemo<OptionType[]>(
    () => getVtmplvOptions(key, verificationTemplateVersions),
    [key, verificationTemplateVersions],
  );

  const verificationsFieldOptions = useMemo<ObjectPropertiesSchema[]>(
    () => getVerificationInputOptions(key),
    [key],
  );
  const updateVtmplvParam = useCallback(
    (vtmplvId: string) =>
      updateActionParams({
        ...params,
        'verification-template-version-id': vtmplvId,
        'verification-template-id': verificationTemplateVersionIdTovTemplateId[vtmplvId],
      }),
    [params, updateActionParams, verificationTemplateVersionIdTovTemplateId],
  );

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Verification Template Version'
        htmlFor='params-create-verification__verification-template-input'
      />
      {vtmplvOptions.length > 0 ? (
        <>
          <ReactSelect
            value={params['verification-template-version-id']}
            options={vtmplvOptions}
            onChange={updateVtmplvParam}
            id='params-create-verification__verification-template'
            hasError={hasErrorFor('verification-template-version-id')}
            disabled={!editable}
          />
          {renderErrorMessageFor('verification-template-version-id')}
          {isCreateVerificationActionWithFieldMapping(action) &&
            params['verification-template-version-id'] && (
              <ParamsLegacyDynamicFlowUpdateFields
                title='Verification Fields'
                fieldsParamKey='verification-to-inquiry-field-mapping'
                fieldMapping={action.params['verification-to-inquiry-field-mapping']}
                usableObjects={usableObjects}
                errors={errors}
                editable={editable}
                updateFieldMapping={(fieldMapping) =>
                  updateActionParams({
                    ...action.params,
                    ...fieldMapping,
                  })
                }
                panelTitle='Configure the mapping of Verification fields (left) from Inquiry fields (right)'
                leftFieldOptions={verificationsFieldOptions}
              />
            )}
          <ParamsCreateVerificationCustom
            action={action}
            editable={editable}
            errors={errors}
            updateActionParams={updateActionParams}
            usableObjects={usableObjects}
            verificationTemplateVersion={verificationTemplateVersion}
          />
        </>
      ) : (
        <Paragraph>No verification template versions of this type found.</Paragraph>
      )}
    </>
  );
};

export default ParamsCreateVerificationV1;
